/**
 * HomeView — the per-client operational workspace, one page. Sticky command
 * column (care score + pillars + full do-next) on the left; collapsible work
 * panels (pipeline, competition, tasks, calendar, reviews, notes,
 * obligations) on the right. The old eight-tab shell is gone: configuration lives at
 * /settings, pulse was dropped, and panel headers carry the at-a-glance
 * summaries the tiles used to. #<panelId> anchors scroll to and briefly
 * highlight a panel.
 */

import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAppStore } from "@/core/store/useAppStore";
import { PageLayout } from "@/components/PageLayout";
import { CommandColumn } from "./components/CommandColumn";
import { ContentPipelinePanel } from "./components/ContentPipelinePanel";
import { CompetitionPanel } from "./components/CompetitionPanel";
import { ObligationsPanel } from "./components/ObligationsPanel";
import { CalendarPanel } from "./components/CalendarPanel";
import { ReviewsPanel } from "./components/ReviewsPanel";
import { NotesPanel } from "./components/NotesPanel";
import { TasksPanel } from "./components/TasksPanel";
import { usePanelCollapse } from "./usePanelCollapse";
import { HomePanelId } from "./homeLinks";

export const HomeView: React.FC = () => {
  const { selectedClient } = useAppStore();
  const location = useLocation();
  const { clientId: urlClientId } = useParams<{ clientId: string }>();
  // Data follows the URL, not the (persisted, possibly stale) store
  // selection — direct loads must never fetch another client's panels.
  const clientId = urlClientId ?? selectedClient?.id ?? "";
  const { collapsed, toggle } = usePanelCollapse(clientId);
  const [highlighted, setHighlighted] = useState<string | null>(null);

  // #<panelId> anchor: scroll to the panel and pulse a highlight ring.
  useEffect(() => {
    const id = location.hash.replace("#", "");
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    el.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
    setHighlighted(id);
    const timer = window.setTimeout(() => setHighlighted(null), 1600);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  if (!clientId) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            No Client Selected
          </h2>
          <p className="text-gray-500">
            Please select a client to view their information
          </p>
        </div>
      </div>
    );
  }

  const ring = (id: HomePanelId) =>
    highlighted === id
      ? "ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-50 rounded-2xl transition-shadow duration-700"
      : "transition-shadow duration-700";

  const panelProps = (id: HomePanelId) => ({
    clientId,
    collapsed: Boolean(collapsed[id]),
    onToggleCollapse: () => toggle(id),
  });

  return (
    <PageLayout>
      {/* Below xl the whole page scrolls; from xl up the cockpit fills the
          space under the header and each column scrolls on its own, so the
          command column and the panel grid share one bottom edge (DD-010
          height model — no 100vh math). */}
      <div className="flex-1 min-h-0 overflow-y-auto xl:overflow-hidden px-4 lg:px-6 py-5">
        <div className="flex flex-col xl:flex-row gap-5 items-start xl:h-full">
          <div className="w-full xl:w-[21rem] xl:shrink-0 xl:max-h-full xl:overflow-y-auto">
            <CommandColumn clientId={clientId} />
          </div>

          {/* Panels size to their content: two columns on xl, equal heights
              within a row, nothing stretched just to fill the canvas. */}
          <div className="w-full xl:flex-1 grid grid-cols-1 xl:grid-cols-2 gap-5 min-w-0 xl:max-h-full xl:overflow-y-auto">
            <div className={`${ring("pipeline")} h-full`}>
              <ContentPipelinePanel {...panelProps("pipeline")} />
            </div>
            <div className={`${ring("competition")} h-full`}>
              <CompetitionPanel {...panelProps("competition")} />
            </div>
            <div className={`${ring("tasks")} h-full`}>
              <TasksPanel {...panelProps("tasks")} />
            </div>
            <div className={`${ring("calendar")} h-full`}>
              <CalendarPanel {...panelProps("calendar")} />
            </div>
            <div className={`${ring("reviews")} h-full`}>
              <ReviewsPanel {...panelProps("reviews")} />
            </div>
            <div className={`${ring("notes")} h-full`}>
              <NotesPanel {...panelProps("notes")} />
            </div>
            <div className={`${ring("obligations")} h-full`}>
              <ObligationsPanel {...panelProps("obligations")} />
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
